import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { getSelf, setLogoutToken } from '@/services/profile.service';
import { resolveUserType } from '@/hooks/data/account/userType';
import { QueryKey } from '@/lib/queryKeys';

export const useAccount = () => {
  const authGetProfileFn = useAuthenticatedQueryFn(getSelf);

  const { isAuthenticated, isLoading: isAuthLoading } = useAuth0();

  const {
    data: profile,
    isLoading: profileLoading,
    refetch: refetchProfile,
  } = useQuery({
    queryKey: [QueryKey.Profile],
    queryFn: async (): Promise<any> => {
      const response = await authGetProfileFn({});

      const collection = response.search.collection;
      const searchIndexes = collection
        ? {
            default: collection,
          }
        : undefined;

      return {
        ...response,
        search: { ...response.search, searchIndexes },
      };
    },
    refetchOnMount: false,
    refetchInterval: false,
    staleTime: Infinity,
    enabled: isAuthenticated,
    retry: false,
  });

  const { isAnonymous, isGuest, isAuthenticatedUser } = useMemo(
    () => resolveUserType(profile, profileLoading),
    [profile, profileLoading],
  );

  // Back-compat alias for capabilities/consumers that deny BOTH guest kinds
  // (anonymous + OTP guest). Prefer the explicit flags above for new code.
  const guest = isAnonymous || isGuest;

  return {
    profile,
    guest,
    isAnonymous,
    isGuest,
    isAuthenticatedUser,
    profileLoading,
    isAuthLoading,
    refetchProfile,
  };
};

export const useLogout = () => {
  const authSetLogoutTokenFn = useAuthenticatedQueryFn(setLogoutToken);

  const logoutUser = async () => {
    await authSetLogoutTokenFn({});
  };

  return {
    logoutUser,
  };
};

export const usePerformLogout = () => {
  const { logout } = useAuth0();
  const { logoutUser } = useLogout();

  const performLogout = useCallback(async () => {
    // First, call the backend logout endpoint while we still have valid tokens
    try {
      await logoutUser();
    } catch {
      // Continue with logout even if backend call fails
    }

    // Clear Auth0 localStorage cache
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('@@auth0spajs@@')) {
        localStorage.removeItem(key);
      }
    });

    // Finally, redirect to Auth0 logout (this will navigate away from the page)
    await logout({
      logoutParams: {
        client_id: process.env.NEXT_PUBLIC_AUTH_CLIENT_ID,
        federated: true,
        returnTo: window.location.origin,
      },
    });
  }, [logout, logoutUser]);

  return {
    performLogout,
  };
};
